// @ts-nocheck
import { SecondaryButton, BUTTON_SIZES } from 'ui/button';
import { useRumAction } from 'services/observability/datadog';
import {
  EventOption,
  PredictiveGoalQuery,
  PropertyOption,
  TemplateEventPopoverContent,
} from 'modules/predictiveGoals/types';
import {
  UpdatedStyledConditionBadge,
  UpdatedQueryBuilderBadgeWrapper,
} from 'modules/predictiveGoals/components/queryBuilder/components/pgQueryBuilderEnhancements/styles';
import { BADGE_VARIANTS } from 'ui/badge';
import { EVENT_OR_PROPERTY_LEADING_TEXT, HAVE_USER_FIELD } from 'modules/predictiveGoals/constants';
import { AndQuery } from './AndQuery';
import {
  AddClauseContainer,
  QueryBuilderDataContainer,
  QueryContentsContainer,
  UpdatedStyledConditionalBadgeWrapper,
  UpdatedAndActionButtons,
  UpdatedButtonWrapper,
} from '../Styled';
import { GOAL_TYPES, CONDITIONAL_TEXT } from '../constants';

type QueryBuilderProps = {
  /** Unique identifier for query builder */
  id: string;
  /** Function run when a change is made to the query, takes in the updated query */
  onUpdate?: (updatedQuery: PredictiveGoalQuery) => void;
  /** Current goal query */
  query: PredictiveGoalQuery;
  /** Selectable events */
  events: EventOption[];
  /** Selectable properties */
  properties: PropertyOption[];
  /** Default duration, should be one month if read from AWS-stored vocabulary JSON */
  defaultDuration: string;
  templatePopoverContent?: TemplateEventPopoverContent;
};

export const QueryBuilder = ({
  defaultDuration,
  events,
  id,
  onUpdate,
  properties,
  query = {},
  templatePopoverContent = {
    action: '',
    result: '',
    eventName: '',
  },
}: QueryBuilderProps) => {
  const andQueries = query.and_clauses || [];
  const sendRumAction = useRumAction();
  const addAndQueryWithEvent = () => {
    sendRumAction('Add query with event');
    onUpdate({
      and_clauses: [
        ...andQueries,
        {
          or_clauses: [{ type: undefined, window: defaultDuration }],
        },
      ],
    });
  };

  const addAndQueryWithProperty = () => {
    sendRumAction('Add query with property');
    onUpdate({
      and_clauses: [
        ...andQueries,
        {
          or_clauses: [{ type: GOAL_TYPES.property, window: defaultDuration }],
        },
      ],
    });
  };

  const updateQuery = (queryIdx, data) => {
    onUpdate({
      and_clauses: andQueries.map((andQuery, idx) => {
        if (idx === queryIdx) {
          return { ...andQuery, or_clauses: data };
        } else {
          return andQuery;
        }
      }),
    });
  };

  const deleteQuery = (queryIdx) => {
    const newAndQueries = [...andQueries];
    newAndQueries.splice(queryIdx, 1);
    onUpdate({ and_clauses: newAndQueries });
  };

  const AND_BADGE_TEXT = CONDITIONAL_TEXT.and.toUpperCase();
  return (
    <div data-test={`${id}_QueryBuilder`}>
      <QueryBuilderDataContainer>
        <QueryContentsContainer>
          {andQueries.map(({ or_clauses: orQueries }, queryIdx) => (
            <div key={queryIdx}>
              {queryIdx > 0 && (
                <UpdatedStyledConditionalBadgeWrapper>
                  {
                    <UpdatedStyledConditionBadge variant={BADGE_VARIANTS.SCHEDULED}>
                      {AND_BADGE_TEXT}
                    </UpdatedStyledConditionBadge>
                  }
                </UpdatedStyledConditionalBadgeWrapper>
              )}
              <div key={queryIdx}>
                <AndQuery
                  id={queryIdx}
                  onUpdate={(updatedQuery) => updateQuery(queryIdx, updatedQuery)}
                  onDelete={() => deleteQuery(queryIdx)}
                  orQueries={orQueries}
                  events={events}
                  properties={properties}
                  duration={defaultDuration}
                  key={queryIdx}
                  templatePopoverContent={templatePopoverContent}
                />
              </div>
            </div>
          ))}
        </QueryContentsContainer>
        <AddClauseContainer>
          {
            <UpdatedAndActionButtons>
              {andQueries.length > 0 && (
                <UpdatedQueryBuilderBadgeWrapper>
                  <UpdatedStyledConditionBadge variant={BADGE_VARIANTS.SCHEDULED}>
                    {AND_BADGE_TEXT}
                  </UpdatedStyledConditionBadge>
                </UpdatedQueryBuilderBadgeWrapper>
              )}
              <UpdatedButtonWrapper>
                <SecondaryButton
                  buttonSize={BUTTON_SIZES.SMALL}
                  onClick={addAndQueryWithEvent}
                  id={'add-event-button'}
                >
                  <span>{EVENT_OR_PROPERTY_LEADING_TEXT.event}</span>
                </SecondaryButton>
                <SecondaryButton
                  buttonSize={BUTTON_SIZES.SMALL}
                  onClick={addAndQueryWithProperty}
                  id="add-property-button"
                >
                  <span>{HAVE_USER_FIELD}</span>
                </SecondaryButton>
              </UpdatedButtonWrapper>
            </UpdatedAndActionButtons>
          }
        </AddClauseContainer>
      </QueryBuilderDataContainer>
    </div>
  );
};
